// src/components/TermsAcceptanceOverlay.jsx
// Fullscreen acceptance gate shown to authenticated users who haven't
// yet accepted TOS + Privacy Policy. Cannot be dismissed without accepting.
import { useState } from "react";

const MONO  = "var(--font-mono, 'IBM Plex Mono', 'Courier New', monospace)";
const DISP  = "var(--font-display, 'Playfair Display', Georgia, serif)";
const MUTED = "#888888";
const TEXT  = "#f0f0f0";
const BDR   = "rgba(255,255,255,0.08)";

export default function TermsAcceptanceOverlay({ onOpenTerms, onOpenPrivacy, onAccept }) {
  const [tosChecked, setTosChecked] = useState(false);
  const [ppChecked,  setPpChecked]  = useState(false);
  const [accepting,  setAccepting]  = useState(false);

  const bothChecked = tosChecked && ppChecked;

  const handleAccept = async () => {
    if (!bothChecked || accepting) return;
    setAccepting(true);
    await onAccept();
    // onAccept sets termsAccepted=true in useAuth → overlay unmounts automatically
    setAccepting(false);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10000,
      background: "var(--bg, #0a0a0a)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: MONO,
    }}>
      <div style={{
        width: "min(480px, 90vw)",
        border: `1px solid ${BDR}`,
        background: "rgba(255,255,255,0.02)",
        padding: "48px",
      }}>
        {/* Eyebrow */}
        <div style={{ fontSize: 9, letterSpacing: ".2em", color: "var(--accent, #3ddc84)", marginBottom: 10 }}>
          // LEGAL AGREEMENT
        </div>

        {/* Headline */}
        <div style={{
          fontFamily: DISP,
          fontSize: 32,
          letterSpacing: ".04em",
          lineHeight: 1.0,
          color: TEXT,
          marginBottom: 16,
        }}>
          TERMS &amp;<br />PRIVACY
        </div>

        <p style={{ fontSize: 11, color: MUTED, lineHeight: 1.8, marginBottom: 32 }}>
          Before accessing Nuvport OS, please read and accept our Terms of Service and Privacy Policy.
        </p>

        {/* Checkbox 1 — TOS */}
        <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer", marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={tosChecked}
            onChange={e => setTosChecked(e.target.checked)}
            style={{ marginTop: 3, accentColor: "var(--accent, #3ddc84)", flexShrink: 0 }}
          />
          <span style={{ fontSize: 11, color: MUTED, lineHeight: 1.7 }}>
            I have read and agree to the{" "}
            <button
              type="button"
              onClick={onOpenTerms}
              style={{
                background: "none", border: "none",
                color: "var(--accent, #3ddc84)", cursor: "pointer",
                fontFamily: MONO, fontSize: 11, padding: 0, textDecoration: "underline",
              }}
            >
              Terms of Service
            </button>
          </span>
        </label>

        {/* Checkbox 2 — Privacy Policy */}
        <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer", marginBottom: 36 }}>
          <input
            type="checkbox"
            checked={ppChecked}
            onChange={e => setPpChecked(e.target.checked)}
            style={{ marginTop: 3, accentColor: "var(--accent, #3ddc84)", flexShrink: 0 }}
          />
          <span style={{ fontSize: 11, color: MUTED, lineHeight: 1.7 }}>
            I have read and agree to the{" "}
            <button
              type="button"
              onClick={onOpenPrivacy}
              style={{
                background: "none", border: "none",
                color: "var(--accent, #3ddc84)", cursor: "pointer",
                fontFamily: MONO, fontSize: 11, padding: 0, textDecoration: "underline",
              }}
            >
              Privacy Policy
            </button>
          </span>
        </label>

        {/* I ACCEPT button */}
        <button
          onClick={handleAccept}
          disabled={!bothChecked || accepting}
          style={{
            width: "100%", padding: "14px 0",
            background: bothChecked ? "var(--accent, #3ddc84)" : "transparent",
            border: `1px solid ${bothChecked ? "var(--accent, #3ddc84)" : BDR}`,
            color: bothChecked ? "#0a0a0a" : "rgba(255,255,255,0.25)",
            fontFamily: MONO, fontSize: 12, letterSpacing: ".14em",
            cursor: bothChecked && !accepting ? "pointer" : "not-allowed",
            transition: "all .15s",
          }}
        >
          {accepting ? "SAVING..." : "I ACCEPT →"}
        </button>

        <div style={{ marginTop: 16, fontSize: 9, color: "rgba(255,255,255,0.2)", textAlign: "center", letterSpacing: ".06em" }}>
          You must accept both to use Nuvport OS
        </div>
      </div>
    </div>
  );
}
